// src/components/NewsletterForm.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackNewsletterSignup } from '../utils/analytics';

interface NewsletterFormProps {
  source?: string;
  variant?: 'default' | 'compact';
}

export function NewsletterForm({ source = 'default', variant = 'default' }: NewsletterFormProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const res = await fetch('/.netlify/functions/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          source,
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to subscribe');
      }

      // Track successful signup
      trackNewsletterSignup(source);

      setStatus('success');
      setEmail('');
      navigate('/thank-you');
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  const baseStyles =
    variant === 'compact'
      ? 'bg-gray-900 p-6 rounded-lg shadow border border-gray-800'
      : 'bg-gray-950 px-8 py-10 rounded-xl shadow-xl border border-gray-800';

  if (status === 'success') return null;

  return (
    <div className={`${baseStyles} overflow-hidden`}>
      <h3 className="text-lg font-semibold mb-3 text-yellow-300">
        {variant === 'compact' ? 'Subscribe for Updates' : 'Stay Updated with PropViper'}
      </h3>
      <p className="text-sm mb-4 text-yellow-100">
        Get the latest prop trading insights, firm reviews, and trading tips delivered to your inbox.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <input
          type="email"
          required
          placeholder="Your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 px-4 py-3 rounded-lg border border-gray-700 bg-gray-800 text-yellow-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="bg-yellow-500 hover:bg-yellow-400 text-black px-6 py-3 rounded-lg font-semibold transition-all disabled:opacity-60"
        >
          {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
        </button>
      </form>

      {status === 'error' && (
        <p className="mt-3 text-red-400 text-sm">Something went wrong. Please try again.</p>
      )}
    </div>
  );
}
